import React, { useState } from 'react'
import { Flex, Text } from '@chakra-ui/react'
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons'
import { BarIcon } from '../../assets/icons/Bar'
import { HomeIcon } from '../../assets/icons/Home'
import { AutomationIcon } from '../../assets/icons/Automation'
import { CustomLink } from './CustomLink'
import { ReportsIcon } from '../../assets/icons/Report'
import { useAlarmsCountContext } from '../../context/AlarmsCountContext'
import BellIcon from '../../assets/icons/Alarm'
import Badge from './Badge/Badge'
import { ControlIcon } from '../../assets/icons/Control'
import { scrollBarStyle } from '../../utils/helpers'
import { useGetAllLocationsQuery } from '../../features/reports/api/reportsApi'
import dayjs from 'dayjs'
import { useGetDeviceGroupListQuery } from '../../features/settings/api/api'

const NavItems = () => {
  const alarmContext = useAlarmsCountContext()
  const [isLocationExpanded, setIsLocationExpanded] = useState(true)
  const [isGroupsExpanded, setIsGroupsExpanded] = useState(false)
  const { data: locations } = useGetAllLocationsQuery({})
  const { data: groups } = useGetDeviceGroupListQuery({})

  const defaultFrom = dayjs().startOf('day').format('YYYY-MM-DD')
  const defaultTo = dayjs().format('YYYY-MM-DD')

  const upperMenuItems = [
    {
      Icon: HomeIcon,
      text: 'Overview',
      to: '/dashboard',
      iconWidth: '6',
      iconHeight: '6',
    },
    {
      Icon: BarIcon,
      text: 'Analysis',
      to: '/analysis',
      iconWidth: '6',
      iconHeight: '5',
    },
    {
      Icon: ControlIcon,
      text: 'Control',
      to: '/control',
      iconWidth: '7',
      iconHeight: '7',
    },
    {
      Icon: AutomationIcon,
      text: 'Automations',
      to: '/automations',
      iconWidth: '9',
      iconHeight: '9',
    },
    {
      Icon: ReportsIcon,
      text: 'Reports',
      to: '/reports/generate',
      iconWidth: '8',
      iconHeight: '8',
    },
    {
      Icon: BellIcon,
      text: 'Alarms',
      to: '/alarms',
      iconWidth: '8',
      iconHeight: '8',
      TextWrapper: ({ children }) => (
        <Flex justifyContent="space-between" w="full" align="center">
          {children}
          {alarmContext.alarmsCount > 0 ? (
            <Badge bg="icon.strong">
              <Text color="white" fontSize="12px" fontWeight="700" p="2px">
                {alarmContext.alarmsCount}
              </Text>
            </Badge>
          ) : null}
        </Flex>
      ),
    },
  ]

  return (
    <Flex direction="column">
      <Flex direction="column" borderBottom="1px solid #DEDFE2">
        {upperMenuItems.map(
          (
            { Icon, text, to, iconWidth = 5, iconHeight = 6, TextWrapper },
            idx: number,
          ) => (
            <CustomLink
              iconWidth={iconWidth}
              iconHeight={iconHeight}
              Icon={Icon}
              to={to}
              key={idx}
              TextWrapper={TextWrapper}
            >
              {text}
            </CustomLink>
          ),
        )}
      </Flex>

      {locations?.results.length > 0 && (
        <>
          <Flex
            py={4}
            align="center"
            cursor="pointer"
            onClick={() => setIsLocationExpanded(!isLocationExpanded)}
          >
            {isLocationExpanded ? (
              <ChevronDownIcon mr={2} boxSize={4} />
            ) : (
              <ChevronRightIcon mr={2} boxSize={4} />
            )}

            <Text fontWeight="bold" fontSize="14px" color="gray.600">
              Locations
            </Text>
          </Flex>

          <Flex direction="column" borderBottom="1px solid #DEDFE2">
            {isLocationExpanded && (
              <Flex
                sx={{ ...scrollBarStyle }}
                direction="column"
                maxH="130px"
                overflowY="auto"
              >
                {locations?.results?.map((location, idx) => (
                  <CustomLink
                    key={`location-${idx}`}
                    to={`/overview/location/?location_id=${location.id}&title=${
                      location.title
                    }${
                      defaultFrom && defaultTo
                        ? `&from=${defaultFrom}&to=${defaultTo}`
                        : ''
                    }`}
                    Icon={() => null}
                    iconWidth={0}
                    iconHeight={0}
                  >
                    {location.title}
                  </CustomLink>
                ))}
              </Flex>
            )}
          </Flex>
        </>
      )}

      {groups?.length > 0 && (
        <>
          <Flex
            py={4}
            align="center"
            cursor="pointer"
            onClick={() => setIsGroupsExpanded(!isGroupsExpanded)}
          >
            {isGroupsExpanded ? (
              <ChevronDownIcon mr={2} boxSize={4} />
            ) : (
              <ChevronRightIcon mr={2} boxSize={4} />
            )}
            <Text fontWeight="bold" fontSize="14px" color="gray.600">
              Groups
            </Text>
          </Flex>

          <Flex direction="column" borderBottom="1px solid #DEDFE2">
            {isGroupsExpanded && (
              <Flex
                direction="column"
                maxH="130px"
                overflowY="auto"
                pr={2}
                sx={{
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a0a0a0',
                  },
                }}
              >
                {groups?.map((group, idx) => (
                  <CustomLink
                    key={`group-${idx}`}
                    to={`overview/group/?group_id=${group.id}&title=${group.name}${
                      defaultFrom && defaultTo
                        ? `&from=${defaultFrom}&to=${defaultTo}`
                        : ''
                    }`}
                    Icon={() => null}
                    iconWidth={0}
                    iconHeight={0}
                  >
                    {group.name}
                  </CustomLink>
                ))}
              </Flex>
            )}
          </Flex>
        </>
      )}
    </Flex>
  )
}

export default NavItems
